import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TFetchParams } from '@/types/fetch.type';
import { TColumn } from '../types/column.type';

export type TGetListColumnResponse = ApiResponse<TColumn[]>;
export type TGetListColumnParams = TFetchParams & {
  spaceId: string;
};

export const getListColumns = async (params: TGetListColumnParams): Promise<TGetListColumnResponse> => {
  const url = `/space-column`;

  return axiosInstance.get(url, { params });
};

export const GET_LIST_COLUMN_QUERY_KEY = 'get-list-column';

export const getListColumnQueryOptions = <TData = TGetListColumnResponse>(
  params: TGetListColumnParams,
  select?: (data: TGetListColumnResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_COLUMN_QUERY_KEY,
      {
        spaceId: params?.spaceId,
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListColumns(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListColumnQuery<TData>(options: {
  params: TGetListColumnParams;
  queryConfig?: QueryConfig<typeof getListColumnQueryOptions<TData>>;
  select: (data: TGetListColumnResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListColumnQuery(options: {
  params: TGetListColumnParams;
  queryConfig?: QueryConfig<typeof getListColumnQueryOptions<TGetListColumnResponse>>;
}): ReturnType<typeof useQuery<TGetListColumnResponse>>;

// Implementation
export function useGetListColumnQuery<TData = TGetListColumnResponse>({
  params,
  queryConfig,
  select,
}: {
  params: TGetListColumnParams;
  queryConfig?: QueryConfig<typeof getListColumnQueryOptions<TData | TGetListColumnResponse>>;
  select?: (data: TGetListColumnResponse) => TData;
}) {
  return useQuery({
    ...getListColumnQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListColumnResponse ? TGetListColumnResponse : TData>>;
}
